import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';
dayjs.extend(timezone);

// Lib
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
// Hooks
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import { useCalendar } from '@/utils/hooks/scheduler/useCalendar';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';

import { dayOfWeek } from '@/lib/date/dayOfWeek';
import { FACEBOOK } from '@/lib/socialProfile';

// Components
import EmptySlot from '@/components/composite/Scheduler/Calendar/EmptySlot';

const WeekDayCells = ({
    activeSocialProfile,
    activeGroup,
    isGroup,
    followersOnlineData,
    bestTimeToPostData,
    timeSlotsData,
    isPast,
    isToday,
    onClick = () => {
        /** */
    },
    dow
}) => {
    const { t } = useTranslation();
    const { calendarData } = useCalendar();
    const { user } = useUser();
    const { formatNumber } = useFormatNumber(user?.settings?.numbers_format ?? 'comma');
    const { formatDateTimeNoTz } = useFormatDateTime();

    // Dow = Day of week
    const fo_week_hours = followersOnlineData.filter((item) => item.day_of_week === dow);
    const bttp_week_hours = bestTimeToPostData.filter((item) => item.day_of_week === dow);

    let isPastSlot = isPast;
    const currentHour = dayjs().tz(user.settings.timezone).format('H');

    const getTooltipContent = (events, followersOnlineData) => {
        if (events.best_time_to_post) {
            return t('scheduler:calendar.events.best_time_to_post_slot_value');
        } else if (events.followers_online) {
            const token = isGroup
                ? 'followers_online_slot_value_group'
                : activeSocialProfile?.social_network_type === FACEBOOK
                  ? 'followers_online_slot_value_facebook'
                  : 'followers_online_slot_value';
            const currentDay = followersOnlineData?.[0]?.day_of_week ?? 0;
            const hourDate = followersOnlineData?.[0]?.hour ?? '00:00';
            const countGroupProfiles = activeGroup?.social_profiles?.length ?? 0;

            return t(`scheduler:calendar.events.${token}`, {
                value:
                    formatNumber(followersOnlineData?.[0]?.value ?? 0, {
                        maximumFractionDigits: 1
                    }) ?? null,
                day: t(`commons:days.${dayOfWeek[currentDay]}`),
                hour: formatDateTimeNoTz(
                    `01/01/2021 ${hourDate}`,
                    `${user?.settings?.time_format}_noSeconds`
                ),
                countGroupProfiles: countGroupProfiles
            });
        }

        return null;
    };

    return (
        <div className="w-full space-y-px">
            {[...Array(24).keys()].map((hour) => {
                // if isToday => Check if hour is lower than the current hour
                if (isToday) {
                    isPastSlot = hour < currentHour;
                }
                const followersOnlineData = calendarData.events.followers_online.active
                    ? fo_week_hours.filter((item) => {
                          const FoHour = parseInt(item.hour.split(':')[0]);
                          return FoHour === hour;
                      })
                    : [];
                const isFollowersOnline =
                    calendarData.events.followers_online.active && followersOnlineData.length > 0;

                const isBestTimeToPost =
                    calendarData.events.best_time_to_post.active &&
                    bttp_week_hours.filter((item) => {
                        const BttpHour = parseInt(item.hour.split(':')[0]);
                        return BttpHour === hour;
                    }).length > 0;
                const isTimeSlot =
                    calendarData.events.time_slots.active &&
                    timeSlotsData &&
                    timeSlotsData[dow].filter((item) => {
                        const timeSlotHour = parseInt(item.hour.split(':')[0]);
                        return timeSlotHour === hour;
                    }).length > 0;
                const timeSlotCount = isTimeSlot
                    ? timeSlotsData[dow].find((item) => {
                          const timeSlotHour = parseInt(item.hour.split(':')[0]);
                          return timeSlotHour === hour;
                      })?.count
                    : 1;

                const events = {
                    followers_online: isFollowersOnline,
                    best_time_to_post: isBestTimeToPost,
                    time_slot: isTimeSlot,
                    time_slot_count: timeSlotCount
                };

                return (
                    <EmptySlot
                        key={hour}
                        hour={hour}
                        isPastSlot={isPastSlot}
                        onClick={onClick}
                        tooltipContent={getTooltipContent(events, followersOnlineData)}
                        events={events}
                    />
                );
            })}
        </div>
    );
};

WeekDayCells.propTypes = {
    followersOnlineData: PropTypes.array.isRequired,
    bestTimeToPostData: PropTypes.array.isRequired,
    timeSlotsData: PropTypes.object,
    isPast: PropTypes.bool.isRequired,
    isToday: PropTypes.bool.isRequired,
    dow: PropTypes.number.isRequired,
    onClick: PropTypes.func
};

export default WeekDayCells;
